import { useCallback, useContext, useMemo, useState } from "react";
import { ContractContext } from "../../../providers/contract";
import styles from "./styles.module.scss";
import BackButton from "../../../components/contract/backButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import CashoutCard from "../CashoutCard";
import {
  EContractType,
  ESenderRole,
  ETrasactionsType,
} from "../../../setup/api/services/contract/models";
import PrimaryButton, {
  PrimaryButtonType,
} from "../../../components/common/PrimaryButton";
import { doAction } from "../../../setup/api/services/contractActions";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import { getUserId } from "../../../setup/auth/oidc";
import { useAlertStore } from "../../../store/alertStore";
import Spinner from "../../../components/common/Spinner";
import { ECONTRACT_STATUS } from "../../../enums";
import ContractNotFound from "../../contract/notFound";

const CashoutDetails = () => {
  const { contract } = useContext(ContractContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showError } = useAlertStore();
  const [isLoading, setIsLoading] = useState(false);

  const handleBackButtonClick = () => {
    const searchParams = new URLSearchParams(location?.search);
    const back = searchParams?.get("back")?.trim();
    if (searchParams?.get("isOperation") === "true") {
      if (back === "settlements") {
        navigate(ROUTES.SETTLEMENTS);
        return;
      }
      navigate(ROUTES.COMPLETED_TRANSFERS);
      return;
    }
    navigate(ROUTES.CASHOUTS);
  };

  const handleContractClick = () => {
    navigate(`${ROUTES.CONTRACT}${contract?.id}`);
  };

  const renderQuickTransactionDetails = useCallback(() => {
    return (
      <div>
        <p className={styles["hint2"]}>{t("lbl_total_amount")}</p>
        <h1 className={classNames(styles["h1"], styles["g900"])}>
          {(contract?.cashOutAmountList
            ? contract.cashOutAmountList
                .reduce((acc, item) => acc + item.cashOutAmount, 0)
                .toFixed(2)
            : "0") +
            " " +
            t("lbl_saudi_currency")}
        </h1>
      </div>
    );
  }, [contract?.cashOutAmountList, t]);

  const maxAmount = useMemo(() => {
    if (!contract) return 0;
    if (contract.type === EContractType.BROKERAGE_CONTRACT)
      return contract.itemPrice;
    return (
      contract.transactions
        ?.filter(
          (t) =>
            (t.type === ETrasactionsType.CUSTOMER_PAY ||
              t.type === ETrasactionsType.MANUAL_BANK_TRANSFER) &&
            t.status === "SUCCESS"
        )
        .reduce((sum, t) => sum + (t.amount || 0), 0) || 0
    );
  }, [contract]);

  const renderMilestoneDetails = useCallback(() => {
    return (
      <div className={styles.amounts_container}>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_total_contract_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["buttonActive"])}>
            {maxAmount.toFixed(2) + " " + t("lbl_saudi_currency")}
          </h3>
        </div>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_waffy_fees_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["g900"])}>
            {contract?.fee.toFixed(2) + " " + t("lbl_saudi_currency")}
          </h3>
        </div>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_transfer_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["buttonActive"])}>
            {(maxAmount - (contract?.fee || 0)).toFixed(2) +
              " " +
              t("lbl_saudi_currency")}
          </h3>
        </div>
      </div>
    );
  }, [contract?.fee, maxAmount, t]);
  const userId = getUserId();

  const handleReSettlementClick = useCallback(async () => {
    if (!userId) return;
    const body = {
      contractId: contract?.id || "",
      userId,
      actorRole: ESenderRole.ADMIN,
      contractAction: EContractActions.ADMIN_REDRAFT_SETTLEMENT_CONTRACT,
    };
    setIsLoading(true);
    try {
      await doAction(body);
      navigate(`${ROUTES.CONTRACT}${contract?.id}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      showError(
        t("lbl_re_settlement_failed") || "Re-settlement failed",
        errorMessage
      );
    } finally {
      setIsLoading(false);
    }
  }, [contract?.id, userId, navigate, showError, t]);

  const renderReSettlementButton = useCallback(() => {
    const cashoutTransactions = contract?.transactions?.filter(
      (transaction) => transaction.beneficiaryId != null
    );

    if (
      contract?.status !== ECONTRACT_STATUS.CASH_OUT_APPROVED ||
      cashoutTransactions?.length
    ) {
      return null;
    }

    return (
      <div>
        <PrimaryButton
          buttonType={PrimaryButtonType.PRIMARY_LIGHT}
          disabled={isLoading}
          style={{
            marginTop: "24px",
            justifySelf: "center",
            width: "20rem",
            fontWeight: 500,
            fontSize: "14px",
          }}
          onClick={handleReSettlementClick}>
          {isLoading ? <Spinner /> : t("lbl_re_settlement")}
        </PrimaryButton>
      </div>
    );
  }, [
    contract?.transactions,
    contract?.status,
    isLoading,
    handleReSettlementClick,
    t,
  ]);

  if (contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT) {
    return <ContractNotFound />;
  }

  return (
    <div className={styles.outer_container}>
      <div className={styles.container}>
        <div className={classNames(styles.top_section, styles.card)}>
          <BackButton onClick={handleBackButtonClick} />
          <div className={styles.content}>
            {contract?.type === EContractType.QUICK_TRANSACTION
              ? renderQuickTransactionDetails()
              : renderMilestoneDetails()}
            <div className={styles.details_container}>
              <p className={styles["hint2"]}>
                {t("lbl_contract_linked_to_transfer")}
              </p>
              <p className={styles.details_link} onClick={handleContractClick}>
                {t("lbl_view_contract")}
              </p>
            </div>
          </div>
        </div>

        {contract?.cashOutAmountList?.map((cashOutAmount, index) => {
          return (
            <CashoutCard
              key={index}
              cashOutAmount={cashOutAmount}
              hideFees={contract?.type !== EContractType.QUICK_TRANSACTION}
              disableCashout={cashOutAmount?.multiCashOut}
            />
          );
        })}
        {renderReSettlementButton()}
      </div>
    </div>
  );
};

export default CashoutDetails;
